export const SYSTEM_USER_DISPLAY_NAME = "System";

export enum UserPermission {
  Administrator = "role:admin",
  PartnerAdministrator = "role:partner-admin",
  ContentAdministrator = "role:content-admin",
  ProcessAdministrator = "role:process-admin",
  CandidateManager = "role:candidate-manager",
  CourseTeacher = "role:course-teacher",
  CourseObserver = "role:course-observer",
  CourseAdministrator = "role:course-admin",
  User = "role:user",
  Candidate = "role:candidate",
  ProcessObserver = "role:process-observer",
  CrmContributor = "role:crm-contributor",
  NoPermissionRequired = "no-permission-required",
  BillingAdministrator = "role:billing-admin",
  TalentpoolUser = "role:talentpool-user",
}

export const AllUserPermissions = Object.values(UserPermission).filter((x) => x != UserPermission.NoPermissionRequired);

export enum MachinePermission {
  SendGrid = "m2m:emailhook",
}

export enum ApiScope {
  Public = "api:public",
}

export enum ApiPermission {
  Read = "m2m:public:read",
  ReadCandidate = "m2m:public:candidate:read",
  WriteCandidate = "m2m:public:candidate:write",
  ReadEmailTemplate = "m2m:migration:emailtemplate:read",
  WriteEmailTemplate = "m2m:migration:emailtemplate:write",
  ReadOrganization = "m2m:public:organization:read",
  WriteOrganization = "m2m:public:organization:write",
  ReadReferenceData = "m2m:public:reference-data:read",
  GenerateBrokerMandate = "m2m:public:broker-mandate:generate",
  SubmitHealthInsuranceApplication = "m2m:public:health-insurance-application:submit",
}

export enum UserRole {
  Administrator = "Administrator",
  PartnerAdministrator = "PartnerAdministrator",
  ContentAdministrator = "ContentAdministrator",
  ProcessAdministrator = "ProcessAdministrator",
  CandidateManager = "CandidateManager",
  CourseObserver = "CourseObserver",
  CourseTeacher = "CourseTeacher",
  CourseAdministrator = "CourseAdministrator",
  User = "User",
  Candidate = "Candidate",
  ProcessObserver = "ProcessObserver",
  EmailReceiverOnly = "EmailReceiverOnly",
  CrmContributor = "CrmContributor",
  BillingAdministrator = "BillingAdministrator",
  TalentpoolUser = "TalentpoolUser",
}

export const ROLE_PERMISSIONS: Record<UserRole, UserPermission[]> = {
  [UserRole.Administrator]: [UserPermission.Administrator],
  [UserRole.PartnerAdministrator]: [UserPermission.PartnerAdministrator],
  [UserRole.ContentAdministrator]: [UserPermission.ContentAdministrator],
  [UserRole.ProcessAdministrator]: [UserPermission.ProcessAdministrator],
  [UserRole.BillingAdministrator]: [UserPermission.BillingAdministrator],
  [UserRole.CandidateManager]: [UserPermission.CandidateManager],
  [UserRole.CourseObserver]: [UserPermission.CourseObserver],
  [UserRole.CourseTeacher]: [UserPermission.CourseTeacher],
  [UserRole.CourseAdministrator]: [UserPermission.CourseAdministrator],
  [UserRole.User]: [UserPermission.User],
  [UserRole.Candidate]: [UserPermission.Candidate],
  [UserRole.ProcessObserver]: [UserPermission.ProcessObserver],
  [UserRole.CrmContributor]: [UserPermission.CrmContributor],
  [UserRole.EmailReceiverOnly]: [],
  [UserRole.TalentpoolUser]: [UserPermission.TalentpoolUser],
};

export const PERMISSION_EQUALITY: Partial<Record<UserPermission, UserPermission>> = {
  [UserPermission.User]: UserPermission.CandidateManager, // if UserPermission.User is requested but User has only UserPermission.CandidateManager, it should be allowed
};

/**
 * A user whose only role is {@link UserRole.EmailReceiverOnly} merely receives emails and has no
 * permissions ({@link ROLE_PERMISSIONS}). Such users are not billed and must therefore never count
 * against a contract's included user accounts nor be blocked when those accounts are exhausted.
 */
export function isEmailReceiverOnly(userRoles: readonly string[] | null | undefined): boolean {
  return userRoles?.length === 1 && userRoles.includes(UserRole.EmailReceiverOnly);
}

export function hasTalentPoolUserPermission(userPermissions: readonly UserPermission[] | null | undefined): boolean {
  return userPermissions?.includes(UserPermission.TalentpoolUser) ?? false;
}
